import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Filter from "./LessonFilter";
import CreateDrawer from "./LessonCreateModal";
import LessonDetailModal from "./LessonDetailModal";
import LessonDownloadModal from "./LessonDownloadModal";
import LessonImportModal from "../courses/LessonImportModal";
import { getAllLessons, getLessonCourses } from "../../../api/elearning/lessons/lessons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import {
  Download,
  Presentation,
  Loader2,
  BookOpen,
  FileText,
  ChevronDown,
  Sparkles,
  LayoutGrid,
  List,
} from "lucide-react";
import logoMark from "../../../assets/images/logo/logo-ima.png";
import { hasPermission } from "../../../utils/permissionUtils";
import {
  downloadLessonDocument,
  presentLessonDocument,
  pickBaiGiangDoc,
  isBaiGiangType,
  isPdfDoc,
  formatDocSize,
  isBaiGiangZipReadyLocally,
} from "./lessonDocumentHelpers";
import { showErrorToast } from "../../../components/ConfirmDialog";
import DocumentDetailModal from "../documents/DocumentDetailModal";
import DocumentPdfPreviewModal from "../documents/DocumentPdfPreviewModal";
import { isPdfDocument } from "../documents/documentViewUtils";
import { startLessonPageTour } from "./lessonPageTour";

const LESSON_PAGE_SIZE = 500;
const VIEW_MODE_KEY = "ima_pwa_lessons_view_mode";

function readStoredViewMode() {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    if (v === "list" || v === "card") return v;
  } catch {
    /* ignore */
  }
  return "card";
}

function normalizeDocuments(lesson) {
  let raw = lesson?.documents;
  if (raw == null) return [];
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  return Array.isArray(raw) ? raw : [];
}

function isGiaoTrinhType(t) {
  return t === "giaotrinh" || t === "giao_trinh";
}

function nonBaiGiangDocs(docs) {
  const arr = Array.isArray(docs) ? docs : [];
  return arr.filter((d) => !isBaiGiangType(d.type));
}

/** Nhãn nút xem: 1 file giáo trình → "Xem giáo trình"; nhiều file → "Xem tài liệu" */
function viewAttachButtonLabel(docs) {
  const nb = nonBaiGiangDocs(docs);
  if (nb.length === 0) return null;
  if (nb.length === 1 && isGiaoTrinhType(nb[0].type)) return "Xem giáo trình";
  return "Xem tài liệu";
}

const LessonCardPage = () => {
  const [lessons, setLessons] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterReloadKey, setFilterReloadKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [courseNameById, setCourseNameById] = useState({});
  const [showDrawer, setShowDrawer] = useState(false);
  const [editLessonId, setEditLessonId] = useState(null);
  const [showImportExcel, setShowImportExcel] = useState(false);
  const [detailLessonId, setDetailLessonId] = useState(null);
  const [busyDocId, setBusyDocId] = useState(null);
  const [downloadModal, setDownloadModal] = useState(null);
  const [detailDocView, setDetailDocView] = useState(null);
  const [previewPdfDoc, setPreviewPdfDoc] = useState(null);
  const [zipCachedByDocId, setZipCachedByDocId] = useState({});
  const [docMenuLessonId, setDocMenuLessonId] = useState(null);
  const [viewMode, setViewMode] = useState(readStoredViewMode);
  const docMenuRef = useRef(null);

  const canQuickTour = hasPermission("elearning.lessons", "R");
  const canViewEduDoc = hasPermission("elearning.documents", "R");
  const canViewLessonDetail = hasPermission("elearning.lessons", "R");

  const openLessonDetail = useCallback((lessonId) => {
    if (!canViewLessonDetail || !lessonId) return;
    setDetailLessonId(lessonId);
  }, [canViewLessonDetail]);

  const handleQuickTour = useCallback(() => {
    window.setTimeout(() => startLessonPageTour(), 80);
  }, []);

  const changeViewMode = useCallback((mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    getLessonCourses()
      .then((rows) => {
        if (cancelled || !Array.isArray(rows)) return;
        const m = {};
        rows.forEach((c) => {
          if (c?.id) m[c.id] = c.name || "";
        });
        setCourseNameById(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCourseLabel = useMemo(() => {
    const id = filters?.course_id;
    if (!id) return null;
    return courseNameById[id] || lessons[0]?.course_name || null;
  }, [filters?.course_id, courseNameById, lessons]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      for (const lesson of lessons) {
        const bg = pickBaiGiangDoc(normalizeDocuments(lesson));
        if (!bg) continue;
        try {
          next[bg.id] = await isBaiGiangZipReadyLocally(bg);
        } catch {
          next[bg.id] = false;
        }
      }
      if (!cancelled) setZipCachedByDocId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [lessons]);

  useEffect(() => {
    if (!docMenuLessonId) return;
    const close = (e) => {
      if (docMenuRef.current && !docMenuRef.current.contains(e.target)) {
        setDocMenuLessonId(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [docMenuLessonId]);

  const fetchLessons = useCallback(async (f = {}) => {
    if (!f.course_id) {
      setLessons([]);
      return;
    }
    setLoading(true);
    try {
      const data = await getAllLessons({
        ...f,
        page: 1,
        size: LESSON_PAGE_SIZE,
      });
      setLessons(data.records || data || []);
    } catch (err) {
      console.error("Lỗi khi tải danh sách bài học:", err);
      showErrorToast("Không tải được danh sách bài học.");
      setLessons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters || {});
    if (!newFilters?.course_id) {
      setLessons([]);
      return;
    }
    fetchLessons(newFilters);
  };

  const markZipCached = (docId, value = true) => {
    if (!docId) return;
    setZipCachedByDocId((prev) => ({ ...prev, [docId]: value }));
  };

  const openDownloadBaiGiang = (lesson) => {
    const docs = normalizeDocuments(lesson);
    const doc = pickBaiGiangDoc(docs);
    if (!doc) {
      showErrorToast("Bài học chưa có tài liệu bài giảng (ZIP).");
      return;
    }
    setDownloadModal({
      doc,
      lesson,
      variant: "bai_giang",
      title: "Tải bài giảng",
      typeHighlight: true,
    });
  };

  const openDoc = (doc) => {
    if (isPdfDoc(doc)) {
      setPreviewPdfDoc(doc);
    } else {
      setDetailDocView(doc);
    }
  };

  const openViewAttachmentSmart = (lesson) => {
    const docs = normalizeDocuments(lesson);
    const nb = nonBaiGiangDocs(docs);
    if (nb.length === 0) {
      showErrorToast("Không có tài liệu đính kèm để xem.");
      return;
    }
    if (nb.length === 1) {
      openDoc(nb[0]);
      return;
    }
    setDocMenuLessonId(lesson.id);
  };

  const runPresent = async (lesson) => {
    const docs = normalizeDocuments(lesson);
    const doc = pickBaiGiangDoc(docs);
    if (!doc) {
      showErrorToast("Chưa có bài giảng để trình chiếu.");
      return;
    }
    try {
      await presentLessonDocument(doc, {
        setBusyId: setBusyDocId,
        localReady: zipCachedByDocId[doc.id] === true,
      });
      const ok = await isBaiGiangZipReadyLocally(doc);
      markZipCached(doc.id, ok);
    } catch {
      /* toast đã có trong helper */
    }
  };

  const hasViewableAttachment = (lesson) => {
    const docs = normalizeDocuments(lesson);
    return nonBaiGiangDocs(docs).length > 0;
  };

  return (
    <div className="w-full min-w-0 px-4 sm:px-6 lg:px-8 py-4">
      <div
        className="mb-6 flex w-full min-w-0 flex-row items-start justify-between gap-3 sm:items-center sm:gap-4"
        data-tour="lesson-header"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
          <div className="shrink-0 rounded-2xl bg-gradient-to-br from-red-600 to-red-800 p-2.5 text-white shadow-lg shadow-red-300/50">
            <FontAwesomeIcon icon={faFileLines} className="text-xl w-6 h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-black tracking-tight text-stone-900 sm:text-2xl">
              Bài học <span className="text-red-700">giảng dạy</span>
            </h1>
            <p className="mt-1 text-[11px] font-bold uppercase tracking-wider text-stone-500">
              {!filters.course_id ? (
                <span className="text-amber-700">
                  {filters.subject_id
                    ? "Chọn khóa học để xem danh sách"
                    : "Chọn môn học rồi khóa học"}
                </span>
              ) : loading ? (
                <span className="inline-flex items-center gap-2 text-stone-400">
                  <Loader2 className="h-3 w-3 animate-spin" /> Đang tải…
                </span>
              ) : (
                <>
                  {selectedCourseLabel ? (
                    <span className="mr-2 font-semibold normal-case tracking-normal text-stone-600">
                      {selectedCourseLabel}
                    </span>
                  ) : null}
                  <span className="text-red-600">{lessons.length}</span> bài học
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="inline-flex h-11 items-center rounded-2xl border border-stone-200 bg-white p-1 shadow-sm"
            role="group"
            aria-label="Kiểu hiển thị"
          >
            <button
              type="button"
              title="Dạng thẻ"
              aria-pressed={viewMode === "card"}
              onClick={() => changeViewMode("card")}
              className={`inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-xs font-bold transition-colors ${
                viewMode === "card"
                  ? "bg-red-700 text-white shadow-sm"
                  : "text-stone-500 hover:bg-stone-50 hover:text-red-700"
              }`}
            >
              <LayoutGrid size={16} strokeWidth={2.25} />
              <span className="hidden sm:inline">Thẻ</span>
            </button>
            <button
              type="button"
              title="Dạng danh sách"
              aria-pressed={viewMode === "list"}
              onClick={() => changeViewMode("list")}
              className={`inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-xs font-bold transition-colors ${
                viewMode === "list"
                  ? "bg-red-700 text-white shadow-sm"
                  : "text-stone-500 hover:bg-stone-50 hover:text-red-700"
              }`}
            >
              <List size={16} strokeWidth={2.25} />
              <span className="hidden sm:inline">Danh sách</span>
            </button>
          </div>
          {canQuickTour ? (
            <div className="flex items-center gap-2" data-tour="lesson-header-guides">
              <button
                type="button"
                onClick={handleQuickTour}
                title="Hướng dẫn nhanh"
                aria-label="Hướng dẫn nhanh"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-red-200 bg-white/90 text-red-800 shadow-sm transition hover:border-red-300 hover:bg-red-50 md:h-auto md:w-auto md:gap-2 md:px-4 md:py-2.5 md:text-xs md:font-bold md:uppercase md:tracking-wide"
              >
                <Sparkles className="h-5 w-5 text-red-600 md:h-4 md:w-4" strokeWidth={2.25} aria-hidden />
                <span className="hidden md:inline">Hướng dẫn nhanh</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <Filter
        onNewClick={() => {
          setEditLessonId(null);
          setShowDrawer(true);
        }}
        onImportExcelClick={() => {
          setShowImportExcel(true);
        }}
        onFilterChange={handleFilterChange}
        reloadKey={filterReloadKey}
        activeFilters={filters}
      />

      {!filters.course_id ? (
        <div className="mt-10 flex w-full flex-col items-center justify-center rounded-3xl border border-dashed border-red-200/80 bg-gradient-to-br from-white via-red-50/40 to-amber-50/50 px-6 py-20 text-center shadow-sm shadow-red-100/40">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-red-600 to-red-800 text-white shadow-lg shadow-red-300/40">
            <BookOpen className="h-8 w-8" strokeWidth={1.75} />
          </div>
          <p className="max-w-md text-sm font-bold text-stone-800">
            {filters.subject_id ? (
              <>
                Chọn <span className="text-red-700">khóa học</span> để tải danh sách bài học.
              </>
            ) : (
              <>
                Chọn <span className="text-red-700">môn học</span> rồi{" "}
                <span className="text-red-700">khóa học</span> để tải danh sách bài học.
              </>
            )}
          </p>
          <p className="mt-2 max-w-md text-xs text-stone-500">
            Xem giáo trình, tải bài giảng và trình chiếu ngay trên lớp.
          </p>
        </div>
      ) : loading ? (
        <div className="mt-12 flex items-center justify-center gap-2 text-sm text-stone-500">
          <Loader2 className="h-5 w-5 animate-spin text-red-600" /> Đang tải bài học…
        </div>
      ) : lessons.length === 0 ? (
        <p className="mt-6 py-16 text-center text-sm text-stone-500">
          Không có bài học nào trong khóa học này (hoặc không khớp bộ lọc).
        </p>
      ) : (
        <div
          className={
            viewMode === "list"
              ? "mt-6 flex w-full flex-col gap-3"
              : "mt-6 grid w-full grid-cols-1 gap-5 md:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4"
          }
        >
          {lessons.map((lesson, index) => {
            const docs = normalizeDocuments(lesson);
            const baiGiang = pickBaiGiangDoc(docs);
            const nb = nonBaiGiangDocs(docs);
            const viewLabel = viewAttachButtonLabel(docs);
            const canViewAttach = hasViewableAttachment(lesson);
            const zipReady = baiGiang ? zipCachedByDocId[baiGiang.id] === true : false;
            const multiAttach = nb.length > 1;
            const menuOpen = docMenuLessonId === lesson.id;
            const sttLabel = String(index + 1).padStart(2, "0");
            const tourAttr = index === 0 ? "lesson-card-actions" : undefined;

            if (viewMode === "list") {
              return (
                <article
                  key={lesson.id}
                  role={canViewLessonDetail ? "button" : undefined}
                  tabIndex={canViewLessonDetail ? 0 : undefined}
                  onClick={() => openLessonDetail(lesson.id)}
                  onKeyDown={(e) => {
                    if (!canViewLessonDetail) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openLessonDetail(lesson.id);
                    }
                  }}
                  className={`flex flex-col gap-3 rounded-2xl border bg-white p-3 shadow-sm transition-colors sm:flex-row sm:items-center sm:gap-4 sm:p-4 ${
                    canViewLessonDetail ? "cursor-pointer" : ""
                  } ${
                    lesson.is_active === false
                      ? "border-stone-200 opacity-70"
                      : "border-red-100/90 hover:border-red-200 hover:shadow-md hover:shadow-red-100/30"
                  }`}
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <LessonListThumb
                      docs={docs}
                      sttLabel={sttLabel}
                      title={lesson.title}
                      thumbnailUrl={lesson.thumbnail_url}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-[10px] font-bold uppercase tracking-wide text-red-700">
                        {lesson.course_name || selectedCourseLabel || "—"}
                      </p>
                      <h3 className="mt-0.5 line-clamp-1 text-sm font-extrabold text-stone-900 sm:text-base">
                        {lesson.title}
                      </h3>
                      <p
                        className="mt-1 line-clamp-1 text-xs text-stone-600 sm:text-sm"
                        title={lesson.description?.trim() || ""}
                      >
                        {lesson.description?.trim() || "—"}
                      </p>
                      <div className="mt-1.5">
                        <DocHintRow docs={docs} />
                      </div>
                    </div>
                  </div>

                  <div
                    className="grid w-full grid-cols-3 gap-1.5 sm:w-auto sm:min-w-[280px] sm:shrink-0"
                    data-tour={tourAttr}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <ListActionBtn
                      icon={FileText}
                      label={viewLabel || "Xem"}
                      disabled={!canViewAttach || !viewLabel}
                      menuOpen={menuOpen}
                      multiAttach={multiAttach}
                      menuItems={nb}
                      menuRef={menuOpen ? docMenuRef : undefined}
                      onClick={() => {
                        if (!canViewAttach || !viewLabel) return;
                        if (multiAttach) {
                          setDocMenuLessonId((id) =>
                            id === lesson.id ? null : lesson.id
                          );
                        } else {
                          openDoc(nb[0]);
                        }
                      }}
                      onPickDoc={(d) => {
                        openDoc(d);
                        setDocMenuLessonId(null);
                      }}
                    />
                    <ListActionBtn
                      icon={Download}
                      label="Tải"
                      tone="amber"
                      disabled={!baiGiang}
                      onClick={() => openDownloadBaiGiang(lesson)}
                    />
                    <ListActionBtn
                      icon={Presentation}
                      label="Chiếu"
                      tone="primary"
                      primary={!!baiGiang && zipReady}
                      disabled={!baiGiang}
                      busy={busyDocId === baiGiang?.id}
                      onClick={() => runPresent(lesson)}
                    />
                  </div>
                </article>
              );
            }

            return (
              <article
                key={lesson.id}
                role={canViewLessonDetail ? "button" : undefined}
                tabIndex={canViewLessonDetail ? 0 : undefined}
                onClick={() => openLessonDetail(lesson.id)}
                onKeyDown={(e) => {
                  if (!canViewLessonDetail) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openLessonDetail(lesson.id);
                  }
                }}
                className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border transition-all duration-300 ${
                  canViewLessonDetail ? "cursor-pointer" : ""
                } ${
                  lesson.is_active === false
                    ? "border-stone-200 bg-white/70 opacity-70 shadow-sm"
                    : "border-red-100/90 bg-white shadow-md shadow-red-200/25 hover:-translate-y-1 hover:border-red-200 hover:shadow-xl hover:shadow-red-200/35"
                }`}
              >
                <div className="h-1.5 w-full bg-gradient-to-r from-red-600 via-rose-500 to-amber-400" />

                <div className="relative flex flex-1 gap-4 bg-gradient-to-br from-[#fff8f6] via-white to-amber-50/40 p-4 sm:p-5">
                  {lesson.thumbnail_url ? (
                    <div
                      className="relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-stone-200 shadow-md shadow-red-300/40 sm:h-[4.5rem] sm:w-[4.5rem]"
                      title={`Thứ tự ${sttLabel}`}
                    >
                      <img
                        src={lesson.thumbnail_url}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                      <span className="absolute bottom-1 left-1 rounded-md bg-black/45 px-1.5 py-0.5 text-[9px] font-black tabular-nums text-white backdrop-blur-[2px]">
                        {sttLabel}
                      </span>
                    </div>
                  ) : (
                    <div
                      className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-red-600 to-red-800 text-white shadow-md shadow-red-300/50 sm:h-[4.5rem] sm:w-[4.5rem]"
                      title={`Thứ tự ${sttLabel}`}
                    >
                      <span className="text-2xl font-black leading-none tracking-tight sm:text-3xl">
                        {sttLabel}
                      </span>
                    </div>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="line-clamp-1 h-4 text-[10px] font-bold uppercase tracking-wide text-red-700">
                      {lesson.course_name || selectedCourseLabel || "—"}
                    </p>
                    <h3 className="mt-1.5 line-clamp-1 min-h-[1.5rem] text-base font-extrabold leading-snug tracking-tight text-stone-900 transition-colors group-hover:text-red-950 sm:min-h-[1.75rem] sm:text-lg">
                      {lesson.title}
                    </h3>
                    <p
                      className="mt-2 line-clamp-2 h-[2.75rem] text-sm leading-[1.375rem] text-stone-600"
                      title={lesson.description?.trim() || ""}
                    >
                      {lesson.description?.trim() || "—"}
                    </p>
                    <div className="mt-2.5 min-h-[1.75rem]">
                      <DocHintRow docs={docs} />
                    </div>
                  </div>
                </div>

                <div
                  className="mt-auto flex flex-col gap-2 bg-gradient-to-b from-white to-[#fff5f2] p-3"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <div
                    className="grid grid-cols-3 gap-1.5"
                    data-tour={tourAttr}
                  >
                    <div
                      className="relative min-w-0"
                      ref={menuOpen ? docMenuRef : undefined}
                    >
                      <button
                        type="button"
                        disabled={!canViewAttach || !viewLabel}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!canViewAttach || !viewLabel) return;
                          if (multiAttach) {
                            setDocMenuLessonId((id) =>
                              id === lesson.id ? null : lesson.id
                            );
                          } else {
                            openDoc(nb[0]);
                          }
                        }}
                        className={`flex h-full min-h-[72px] w-full flex-col items-center justify-center gap-0.5 rounded-2xl border px-1 py-2 text-[8px] font-bold uppercase leading-tight tracking-wide transition-all ${
                          !canViewAttach || !viewLabel
                            ? "cursor-not-allowed border-stone-100 bg-stone-50 text-stone-300"
                            : "border-rose-200/80 bg-gradient-to-b from-white to-rose-50 text-rose-900 shadow-sm hover:border-rose-300 hover:shadow-md"
                        }`}
                      >
                        <FileText className="h-4 w-4 shrink-0 text-rose-700" strokeWidth={2} />
                        <span className="px-0.5 text-center">{viewLabel || "—"}</span>
                        {multiAttach ? (
                          <ChevronDown className="h-3 w-3 opacity-70" strokeWidth={2.5} />
                        ) : null}
                      </button>
                      {menuOpen && multiAttach ? (
                        <ul className="absolute left-0 right-0 top-full z-30 mt-1 max-h-48 overflow-auto rounded-xl border border-red-100 bg-white py-1 shadow-xl">
                          {nb.map((d) => (
                            <li key={d.id}>
                              <button
                                type="button"
                                className="w-full truncate px-3 py-2 text-left text-xs font-medium text-stone-800 hover:bg-red-50"
                                title={d.file_name}
                                onClick={() => {
                                  openDoc(d);
                                  setDocMenuLessonId(null);
                                }}
                              >
                                {d.file_name || "Tài liệu"}
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <TriBtn
                      icon={Download}
                      label="Tải bài giảng"
                      tone="amber"
                      disabled={!baiGiang}
                      onClick={() => openDownloadBaiGiang(lesson)}
                    />
                    <TriBtn
                      icon={Presentation}
                      label="Trình chiếu"
                      tone="primary"
                      primary={!!baiGiang && zipReady}
                      disabled={!baiGiang}
                      busy={busyDocId === baiGiang?.id}
                      onClick={() => runPresent(lesson)}
                    />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <CreateDrawer
        isOpen={showDrawer || Boolean(editLessonId)}
        lessonId={editLessonId}
        onClose={() => {
          setShowDrawer(false);
          setEditLessonId(null);
        }}
        onSaved={() => {
          if (filters.course_id) fetchLessons(filters);
          setShowDrawer(false);
          setEditLessonId(null);
        }}
      />

      <LessonImportModal
        isOpen={showImportExcel}
        onClose={() => setShowImportExcel(false)}
        initialSubjectId={filters.subject_id || ""}
        initialCourseId={filters.course_id || ""}
        onSuccess={(importedCourseId, importedSubjectId) => {
          setFilterReloadKey((k) => k + 1);
          if (importedCourseId) {
            const next = {
              ...filters,
              subject_id: importedSubjectId || filters.subject_id || "",
              course_id: importedCourseId,
            };
            setFilters(next);
            fetchLessons(next);
          } else if (filters.course_id) {
            fetchLessons(filters);
          }
        }}
      />

      <LessonDetailModal
        lessonId={detailLessonId}
        isOpen={Boolean(detailLessonId)}
        onClose={() => setDetailLessonId(null)}
        onEdit={(id) => {
          setDetailLessonId(null);
          setEditLessonId(id);
        }}
        onDeleted={() => {
          if (filters.course_id) fetchLessons(filters);
        }}
        onUpdated={() => {
          if (filters.course_id) fetchLessons(filters);
        }}
      />

      <LessonDownloadModal
        isOpen={Boolean(downloadModal)}
        onClose={() => setDownloadModal(null)}
        title={downloadModal?.title || "Tải bài giảng"}
        courseName={downloadModal?.lesson?.course_name || "—"}
        lessonTitle={downloadModal?.lesson?.title || "—"}
        contentLine={
          String(downloadModal?.lesson?.description ?? "")
            .trim()
            .slice(0, 120) ||
          downloadModal?.doc?.file_name ||
          "—"
        }
        typeLabel="BÀI GIẢNG"
        typeHighlight
        fileSize={formatDocSize(downloadModal?.doc?.file_size)}
        localVersionReady={
          downloadModal?.doc ? zipCachedByDocId[downloadModal.doc.id] === true : false
        }
        onStartDownload={async ({ onProgress } = {}) => {
          if (!downloadModal?.doc) return false;
          const saved = await downloadLessonDocument(downloadModal.doc, { onProgress });
          if (saved) {
            const ready = await isBaiGiangZipReadyLocally(downloadModal.doc);
            markZipCached(downloadModal.doc.id, ready);
          }
          return saved;
        }}
      />

      {previewPdfDoc ? (
        <DocumentPdfPreviewModal
          document={previewPdfDoc}
          onClose={() => setPreviewPdfDoc(null)}
        />
      ) : null}

      {detailDocView ? (
        <DocumentDetailModal
          document={detailDocView}
          onClose={() => setDetailDocView(null)}
          onView={() => {
            const d = detailDocView;
            if (d && isPdfDocument(d)) {
              setDetailDocView(null);
              setPreviewPdfDoc(d);
            }
          }}
          onEdit={() => {}}
          onDelete={() => {}}
          canView={canViewEduDoc}
          canEdit={false}
          canDelete={false}
        />
      ) : null}
    </div>
  );
};

function LessonListThumb({ docs, sttLabel, title, thumbnailUrl }) {
  const arr = Array.isArray(docs) ? docs : [];
  const hasZip = arr.some((d) => isBaiGiangType(d.type));
  const hasPdf = arr.some((d) => isPdfDoc(d));
  const Icon = hasZip ? Presentation : hasPdf ? FileText : BookOpen;
  const kindLabel = hasZip ? "Bài giảng" : hasPdf ? "PDF" : "Bài học";
  const gradient = hasZip
    ? "from-red-600 via-rose-600 to-amber-500"
    : hasPdf
      ? "from-amber-500 via-orange-500 to-red-600"
      : "from-stone-600 via-stone-700 to-red-800";

  if (thumbnailUrl) {
    return (
      <div
        className="relative h-[4.25rem] w-[4.25rem] shrink-0 overflow-hidden rounded-2xl bg-stone-200 shadow-md shadow-red-200/40 ring-1 ring-black/5 sm:h-[4.75rem] sm:w-[4.75rem]"
        title={`${kindLabel} · #${sttLabel}${title ? ` · ${title}` : ""}`}
      >
        <img
          src={thumbnailUrl}
          alt=""
          className="h-full w-full object-cover"
          loading="lazy"
        />
        <span className="absolute bottom-1 left-1 rounded-md bg-black/45 px-1.5 py-0.5 text-[9px] font-black tabular-nums text-white backdrop-blur-[2px]">
          {sttLabel}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`relative h-[4.25rem] w-[4.25rem] shrink-0 overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} shadow-md shadow-red-200/40 ring-1 ring-black/5 sm:h-[4.75rem] sm:w-[4.75rem]`}
      title={`${kindLabel} · #${sttLabel}${title ? ` · ${title}` : ""}`}
    >
      <img
        src={logoMark}
        alt=""
        className="pointer-events-none absolute -right-2 -top-1 h-12 w-12 object-contain opacity-[0.18] sm:h-14 sm:w-14"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.45), transparent 55%)",
        }}
      />
      <div className="relative flex h-full flex-col items-center justify-center gap-1 px-1.5 pt-1">
        <Icon className="h-6 w-6 text-white drop-shadow-sm sm:h-7 sm:w-7" strokeWidth={2} />
        <span className="max-w-full truncate text-[8px] font-bold uppercase tracking-wide text-white/90">
          {kindLabel}
        </span>
      </div>
      <span className="absolute bottom-1 left-1 rounded-md bg-black/40 px-1.5 py-0.5 text-[9px] font-black tabular-nums text-white backdrop-blur-[2px]">
        {sttLabel}
      </span>
    </div>
  );
}

function DocHintRow({ docs }) {
  const arr = Array.isArray(docs) ? docs : [];
  const zip = arr.some((d) => isBaiGiangType(d.type));
  const pdf = arr.some((d) => isPdfDoc(d));
  const otherNonPdf = arr.some((d) => !isBaiGiangType(d.type) && !isPdfDoc(d));
  if (!zip && !pdf && !otherNonPdf) {
    return (
      <p className="flex h-7 items-center text-[10px] leading-none text-stone-400">
        Chưa gắn tài liệu
      </p>
    );
  }
  return (
    <div className="flex h-7 flex-wrap items-center gap-1.5 overflow-hidden">
      {zip ? (
        <span className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-[9px] font-bold uppercase leading-none text-red-800 shadow-sm">
          Bài giảng ZIP
        </span>
      ) : null}
      {pdf ? (
        <span className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-[9px] font-bold uppercase leading-none text-amber-900 shadow-sm">
          PDF
        </span>
      ) : null}
      {otherNonPdf ? (
        <span className="rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-1 text-[9px] font-bold uppercase leading-none text-stone-700 shadow-sm">
          Tài liệu kèm
        </span>
      ) : null}
    </div>
  );
}

function ListActionBtn({
  icon: Icon,
  label,
  disabled,
  busy,
  primary,
  tone = "neutral",
  onClick,
  multiAttach,
  menuOpen,
  menuItems,
  menuRef,
  onPickDoc,
}) {
  let enabledClass =
    "border-stone-200 bg-white text-stone-700 hover:border-red-300 hover:bg-red-50/60 hover:text-red-900";
  if (tone === "amber") {
    enabledClass =
      "border-amber-200 bg-amber-50 text-amber-950 hover:border-amber-300";
  }
  if (tone === "primary" || primary) {
    enabledClass = primary
      ? "border-red-800 bg-gradient-to-b from-red-600 to-red-800 text-white hover:from-red-500 hover:to-red-700"
      : "border-red-200 bg-red-50 text-red-900 hover:border-red-300";
  }

  return (
    <div className="relative min-w-0" ref={menuRef}>
      <button
        type="button"
        disabled={disabled || busy}
        onClick={onClick}
        title={label}
        className={`flex h-11 w-full items-center justify-center gap-1 rounded-xl border px-1.5 text-[10px] font-bold uppercase tracking-wide transition-all ${
          disabled
            ? "cursor-not-allowed border-stone-100 bg-stone-50 text-stone-300"
            : enabledClass
        }`}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
        ) : (
          <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.25} />
        )}
        <span className="truncate">{label}</span>
        {multiAttach ? <ChevronDown className="h-3 w-3 shrink-0 opacity-70" /> : null}
      </button>
      {menuOpen && multiAttach && Array.isArray(menuItems) ? (
        <ul className="absolute left-0 right-0 top-full z-30 mt-1 max-h-48 overflow-auto rounded-xl border border-red-100 bg-white py-1 shadow-xl">
          {menuItems.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                className="w-full truncate px-3 py-2 text-left text-xs font-medium text-stone-800 hover:bg-red-50"
                title={d.file_name}
                onClick={() => onPickDoc?.(d)}
              >
                {d.file_name || "Tài liệu"}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function TriBtn({ icon: Icon, label, disabled, busy, primary, tone = "neutral", onClick }) {
  let enabledClass =
    "border-stone-200/90 bg-white text-stone-800 shadow-sm hover:border-red-300 hover:bg-gradient-to-b hover:from-white hover:to-red-50/60 hover:text-red-900";
  if (tone === "amber") {
    enabledClass =
      "border-amber-200 bg-gradient-to-b from-amber-50 to-orange-50 text-amber-950 shadow-sm hover:border-amber-300 hover:shadow-md";
  }
  if (tone === "primary" || primary) {
    enabledClass = primary
      ? "border-red-800 bg-gradient-to-b from-red-600 to-red-800 text-white shadow-md shadow-red-700/30 hover:from-red-500 hover:to-red-700 hover:shadow-lg"
      : "border-red-200 bg-gradient-to-b from-white to-red-50 text-red-900 shadow-sm hover:border-red-300 hover:shadow-md";
  }

  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      title={label}
      className={`flex min-h-[72px] flex-col items-center justify-center gap-1 rounded-2xl border px-1 py-2 text-[8px] font-bold uppercase leading-tight tracking-wide transition-all ${
        disabled
          ? "cursor-not-allowed border-stone-100 bg-stone-50/90 text-stone-300"
          : enabledClass
      }`}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
      ) : (
        <Icon
          className={`h-4 w-4 shrink-0 ${
            disabled
              ? ""
              : primary
                ? "text-white"
                : tone === "amber"
                  ? "text-amber-700"
                  : "text-red-700"
          }`}
          strokeWidth={2}
        />
      )}
      <span className="px-0.5 text-center">{label}</span>
    </button>
  );
}

export default LessonCardPage;
